import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, UserPlus, Sparkles, Hammer, Gem, FlaskConical, Dumbbell, Palette, Moon, X, Search, ArrowUpDown, Pencil, Skull, Stethoscope, Eye, EyeOff, ShieldCheck, ShieldOff, Megaphone, Crown, Gavel, Package, ChevronLeft, ChevronRight, ExternalLink, Shield, ShoppingBag, Clock } from "lucide-react";

// Custom Ludus Icon - fills container
const LudusIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/ludus-icon.png" 
    alt="Ludus" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

// Custom Helmet Icon for Roster - fills container
const HelmetIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/helmet-icon.png" 
    alt="Roster" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

// Server Clock Component - shows real-time server time in America/Chicago (Central Time)
const ServerClock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    // Convert to America/Chicago timezone (Central Time) - matches backend GAME_TZ
    const serverTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
    
    return serverTime;
  };

  return (
    <div className="text-zinc-400 text-xs">
      <span className="font-mono">Server Time: {formatTime(time)} CT</span>
    </div>
  );
};
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { LifecycleBadge } from "@/components/GladiatorCard";
import RosterDowngradeModal from "@/components/RosterDowngradeModal";
import ReferralPanel from "@/components/ReferralPanel";
import BeastIcon from "@/components/BeastIcon";
import { AboutBox } from "@/components/AboutBox";
import Modal from "@/components/Modal";
import { SKILL_META, RARITY, CLASS_META, LUDUS_IMAGE } from "@/lib/game";
import TraitSelectionModal from "@/components/TraitSelectionModal";
import { DashboardChampionRows } from "@/components/DashboardChampionRows";
import NewPlayerChecklist from "@/components/NewPlayerChecklist";
import TraitAssignmentBanner from "@/components/TraitAssignmentBanner";
import GoldenCrownIcon from "@/components/GoldenCrownIcon";
import { OreRushEventNotice } from "@/components/OreRushEventNotice";

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-100">{value}</div>
    </div>
  );
}

function NewAccountFragmentBonus({ bonus }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!bonus?.active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, [bonus?.active]);
  if (!bonus?.active || !bonus?.until) return null;
  const remaining = Math.max(0, new Date(bonus.until).getTime() - now);
  if (!remaining) return null;
  const hours = Math.ceil(remaining / 3600000);
  return <section data-testid="new-account-fragment-bonus" className="mb-6 border-l-2 border-emerald-500 bg-emerald-950/20 px-4 py-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-emerald-200">New Account Bonus</div>
        <p data-testid="new-account-fragment-bonus-copy" className="mt-1 text-sm text-zinc-100"><span className="font-semibold text-emerald-200">{bonus.mult}× fragment rewards</span> are active for your first {bonus.days} days in the arena.</p>
      </div>
      <span data-testid="new-account-fragment-bonus-countdown" className="text-xs tabular-nums text-emerald-200">{hours}h remaining</span>
    </div>
  </section>;
}

// Vivarium Dashboard Link Component
function VivariumDashboardLink({ vivariumData, loading }) {
  const navigate = useNavigate();

  if (loading) return null;

  const vivarium = vivariumData?.vivarium;
  const beastCount = vivariumData?.beast_count || 0;
  const capacity = vivarium?.capacity || 3;
  const isBuilt = vivarium?.built;

  return (
    <button
      onClick={() => navigate("/vivarium")}
      data-testid="vivarium-dashboard-link"
      className="relative h-full overflow-hidden rounded-sm border border-teal-800/40 bg-teal-950/10 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-teal-700/60 hover:bg-teal-950/20 cursor-pointer"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <BeastIcon size={11} className="text-teal-400" /> {isBuilt ? "Vivarium" : "Build a Vivarium"}
        </span>
        {isBuilt && (
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-teal-700 bg-teal-900/30 text-teal-300 rounded-sm">
            {beastCount}/{capacity}
          </span>
        )}
      </div>
      <div className="font-cinzel text-teal-200 tracking-wide">
        {isBuilt ? "Manage Your Beasts" : "Capture Wild Creatures"}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
        {isBuilt ? `${beastCount} beasts captured` : "Requires 500 common + 100 uncommon fragments"}
      </div>
      <p className="text-sm text-zinc-400 mt-2">
        {isBuilt 
          ? "Equip exotic beasts to your gladiators for powerful stat bonuses in Ranked PvP."
          : "Build a Vivarium to capture and train wild beasts that empower your gladiators in battle."
        }
      </p>
    </button>
  );
}

// Doctore Sanctum Dashboard Link Component
function SanctumDashboardLink({ sanctumData, loading }) {
  const navigate = useNavigate();

  if (loading) return null;

  const doctoreCount = sanctumData?.doctores?.length || 0;
  const maxSlots = sanctumData?.sanctum_slots || 1;

  return (
    <button
      onClick={() => navigate('/sanctum')}
      data-testid="sanctum-dashboard-link"
      className="relative h-full overflow-hidden rounded-sm border border-amber-800/40 bg-amber-950/10 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-amber-700/60 hover:bg-amber-950/20 cursor-pointer"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <Shield size={11} className="text-amber-400" /> Doctore's Sanctum
        </span>
        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-700 bg-amber-900/30 text-amber-300 rounded-sm">
          {doctoreCount}/{maxSlots}
        </span>
      </div>
      <div className="font-cinzel text-amber-200 tracking-wide">
        Specialized Training
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
        {doctoreCount > 0 ? `${doctoreCount} master trainers` : "Convert retired gladiators into trainers"}
      </div>
      <p className="text-sm text-zinc-400 mt-2">
        Retired gladiators with 50+ battles become doctores who teach specialized training to boost specific skills.
      </p>
    </button>
  );
}

function BestGear({ gear, onClick }) {
  const meta = RARITY[gear.rarity] || RARITY.common;
  return (
    <div data-testid="best-gear-card" onClick={onClick}
      className={`relative overflow-hidden border ${meta.border} ${meta.bg} p-4 cursor-pointer hover:-translate-y-0.5 transition-all`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Sparkles size={11} className={meta.text} /> Best Forged Gear</span>
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${meta.text} ${meta.border}`}>{meta.label}</span>
      </div>
      <div className={`font-cinzel ${meta.text} tracking-wide truncate`}>{gear.name}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{gear.slot}{gear.cls ? ` · ${gear.cls}` : " · any class"}</div>
      <div className="flex flex-wrap gap-2 mt-2">
        {Object.entries(gear.base_mods || {}).map(([k, v]) => (
          <span key={k} className="text-[10px] text-emerald-400">+{v} {SKILL_META[k]?.label || k}</span>
        ))}
      </div>
      {(gear.affixes || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {gear.affixes.map((af, i) => (
            <span key={i} className="text-[10px] text-violet-300 border border-violet-800/40 bg-violet-950/20 px-1.5 py-0.5 rounded-sm">{af.display}</span>
          ))}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-2">
        {gear.equipped_on ? `Equipped · ${gear.equipped_on}` : "In storage — visit the Blacksmith"}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [dailyLudusAlert, setDailyLudusAlert] = useState(false);
  const [merchantStatus, setMerchantStatus] = useState(null);
  const [downgrade, setDowngrade] = useState(null);
  const [playTest, setPlayTest] = useState(false);
  const [sel, setSel] = useState(null);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("dashboard_sort") || "age");
  const [renameVal, setRenameVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [scoutOpen, setScoutOpen] = useState(false);
  const [scoutNoConfirm, setScoutNoConfirm] = useState(() => localStorage.getItem("scout_no_confirm") === "1");
  const [inventoryPage, setInventoryPage] = useState(1);
  const [needsTraits, setNeedsTraits] = useState([]);
  const [traitModalGlad, setTraitModalGlad] = useState(null);
  const [consumables, setConsumables] = useState([]);
  const [vivariumData, setVivariumData] = useState(null);
  const [vivariumLoading, setVivariumLoading] = useState(true);
  const [sanctumData, setSanctumData] = useState(null);
  const [sanctumLoading, setSanctumLoading] = useState(true);

  const itemsPerPage = 4;
  const totalInventoryPages = Math.ceil(consumables.length / itemsPerPage);
  const paginatedInventory = consumables.slice((inventoryPage - 1) * itemsPerPage, inventoryPage * itemsPerPage);
  const totalConsumableCount = consumables.reduce((sum, item) => sum + Number(item.count || 0), 0);

  const dismissRestNotice = async () => {
    setData((p) => ({ ...p, inactivity_rest: null }));
    try { await api.post("/dashboard/dismiss-rest-notice"); } catch { /* non-critical */ }
  };

  const changeSortBy = (sort) => {
    setSortBy(sort);
    localStorage.setItem("dashboard_sort", sort);
  };

  const openGlad = (g) => { setSel(g); setRenameVal(""); };
  
  const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

  const doRename = async () => {
    if (!renameVal.trim()) return;
    setBusy(true);
    try {
      const { data: updated } = await api.put(`/gladiators/${sel.id}/rename`, { name: renameVal.trim() });
      toast.success("Gladiator renamed");
      setData((prev) => ({ ...prev, active: prev.active.map((x) => x.id === updated.id ? updated : x) }));
      setSel(updated); setRenameVal("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doScout = async () => {
    setScoutOpen(false);
    if (scoutNoConfirm) localStorage.setItem("scout_no_confirm", "1");
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${sel.id}/scout`);
      setSel(result.gladiator);
      setData((prev) => ({ ...prev, active: prev.active.map((x) => x.id === result.gladiator.id ? result.gladiator : x) }));
      toast.success(result.gladiator.potential_band?.exact
        ? `Scouting complete — ${result.gladiator.name}'s potential is ${result.gladiator.potential_grade}!`
        : `Scouts report back — potential now ${result.gladiator.potential_grade}`);
      refreshUser?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleSkills = async () => {
    const ns = sel.show_skills === false;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${sel.id}/skills-visibility`, { show: ns });
      setSel((s) => ({ ...s, show_skills: result.show_skills }));
      setData((prev) => ({ ...prev, active: prev.active.map((x) => x.id === sel.id ? { ...x, show_skills: result.show_skills } : x) }));
      toast.success(result.show_skills ? "Skills are now public" : "Skills hidden from the public");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleProtect = async () => {
    const np = !sel.protected_from_challenges;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/gladiators/${sel.id}/protect`, { protect: np });
      setSel((s) => ({ ...s, protected_from_challenges: result.protected_from_challenges }));
      setData((prev) => ({ ...prev, active: prev.active.map((x) => x.id === sel.id ? { ...x, protected_from_challenges: result.protected_from_challenges } : x) }));
      toast.success(result.protected_from_challenges ? `${sel.name} is now protected from auto-accepted challenges` : `${sel.name} will auto-accept challenges again`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const handleAssignTraits = async (traits) => {
    if (!traitModalGlad) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${traitModalGlad.id}/assign-traits`, traits);
      setData((prev) => ({
        ...prev,
        active: prev.active.map((g) => g.id === data.id ? data : g)
      }));
      if (sel?.id === data.id) setSel(data);
      setNeedsTraits((prev) => prev.filter((g) => g.id !== traitModalGlad.id));
      setTraitModalGlad(null);
      toast.success(`Traits assigned to ${data.name}!`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/dashboard").then((r) => {
      setData(r.data);
      const seen = JSON.parse(localStorage.getItem("seen_retirements") || "[]");
      const alerts = r.data.retirement_alerts || [];
      const fresh = alerts.filter((a) => !seen.includes(a.id));
      fresh.forEach((a) => toast(`⚰ ${a.subject}`, { description: "Check your Hall of the Retired." }));
      if (fresh.length) localStorage.setItem("seen_retirements", JSON.stringify([...seen, ...fresh.map((a) => a.id)]));
    }).catch(() => {});
    api.get("/vip/downgrade-status").then((r) => { if (r.data.needs_choice) setDowngrade(r.data); }).catch(() => {});
    api.get("/play-test").then((r) => setPlayTest(!!r.data.active)).catch(() => {});
    // Fetch gladiators needing traits
    api.get("/gladiators/needs-traits").then((r) => {
      console.log('[TRAITS DASHBOARD] API response:', r.data);
      setNeedsTraits(r.data.gladiators || []);
    }).catch((e) => {
      console.error('[TRAITS DASHBOARD] API error:', e);
      setNeedsTraits([]);
    });
    // Fetch consumables for inventory
    api.get("/inventory").then((r) => {
      setConsumables(r.data.consumables || []);
    }).catch(() => {});
    // Check merchant status
    api.get("/merchant/status").then((r) => setMerchantStatus(r.data)).catch(() => setMerchantStatus(null));
    api.get("/vivarium")
      .then((r) => setVivariumData(r.data))
      .catch(() => setVivariumData(null))
      .finally(() => setVivariumLoading(false));
    api.get("/sanctum")
      .then((r) => setSanctumData(r.data))
      .catch(() => setSanctumData(null))
      .finally(() => setSanctumLoading(false));
    const refreshDailyLudusAlert = () => api.get("/daily-ludus/streak-status")
      .then((r) => setDailyLudusAlert(!!r.data?.daily_streak?.claimable || !!r.data?.journey_reward_claimable))
      .catch(() => setDailyLudusAlert(false));
    refreshDailyLudusAlert();
    window.addEventListener("lanista:refresh-daily-ludus-alert", refreshDailyLudusAlert);
    return () => window.removeEventListener("lanista:refresh-daily-ludus-alert", refreshDailyLudusAlert);
  }, []);

  const reload = () => {
    setDowngrade(null);
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  };

  if (!data) return <Loader label="Surveying your ludus…" />;
  if (downgrade) return <RosterDowngradeModal data={downgrade} onResolved={reload} />;

  return (
    <div className="relative">
      {/* Textured background layers */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Noise/grain texture */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />
        
        {/* Diagonal scratch marks */}
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 100px, rgba(200, 200, 200, 0.1) 100px, rgba(200, 200, 200, 0.1) 101px),
                           repeating-linear-gradient(-45deg, transparent, transparent 150px, rgba(200, 200, 200, 0.05) 150px, rgba(200, 200, 200, 0.05) 151px)`
        }} />
        
        {/* Arena stone texture */}
        <div className="absolute inset-0 opacity-[0.1]" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(0, 0, 0, 0.08) 0%, transparent 50%)`,
          backgroundSize: '350px 350px'
        }} />
      </div>
      
      <div className="relative z-10">
      <NewAccountFragmentBonus bonus={user?.new_account_fragment_bonus} />
      <OreRushEventNotice testId="dashboard-ore-rush-banner" />
      <section data-testid="dashboard-command-header" className="relative mb-8 isolate min-h-[20rem] overflow-hidden border-y border-amber-700/40">
        <img data-testid="dashboard-training-yard-image" src={LUDUS_IMAGE} alt="Gladiators training in the Ludus yard" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/65 to-black/25" />
        <div className="relative flex min-h-[20rem] max-w-4xl flex-col justify-end px-6 py-8 sm:px-10 sm:py-10">
          <h1 data-testid="dashboard-greeting" className="mt-3 font-cinzel text-4xl text-zinc-50 sm:text-5xl lg:text-6xl">Salve, {user?.name}</h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/25 pt-4 text-xs uppercase tracking-[0.18em] text-zinc-300">
            <span data-testid="dashboard-active-count">{data.active.length} active champions</span>
            <span data-testid="dashboard-ready-count">{data.active.filter((gladiator) => gladiator.fight_ready !== false).length} arena-ready</span>
            <button data-testid="dashboard-daily-ludus-link" onClick={() => navigate("/")} className="flex min-w-[13rem] flex-col border-2 border-amber-300 bg-amber-400 px-5 py-3 text-left text-zinc-950 shadow-[0_0_28px_rgba(245,158,11,0.28)] transition-colors duration-200 hover:bg-amber-300"><span className="font-cinzel text-base uppercase tracking-[0.14em]">Daily Ludus</span><span data-testid="dashboard-daily-ludus-alert" className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em]">{dailyLudusAlert ? "Reward ready — open now" : "Review activity and rewards"}</span></button>
          </div>
        </div>
      </section>

      {/* Trait Assignment Banner */}
      <TraitAssignmentBanner count={needsTraits.length} onClick={() => {
        if (needsTraits.length === 0) return;
        const glad = data?.active?.find(g => needsTraits.some(n => n.id === g.id));
        if (glad) setTraitModalGlad(glad);
      }} />

      {/* Merchant Banner (Saturdays only) */}
      {merchantStatus?.active && (
        <div data-testid="merchant-banner" onClick={() => navigate('/equipment?tab=merchant')}
          className="mb-6 flex items-center gap-3 border border-orange-500/60 bg-gradient-to-r from-orange-950/30 to-amber-950/20 px-4 py-3 rounded-sm cursor-pointer hover:border-orange-400/80 transition-all hover:-translate-y-0.5">
          <ShoppingBag size={18} className="text-orange-400 shrink-0 animate-pulse" />
          <div className="flex-1">
            <p className="text-sm text-orange-100">
              <span className="font-cinzel uppercase tracking-wide text-orange-300">🎪 The Traveling Merchant has arrived!</span>
              {" "}Exclusive items available for 24 hours only. Visit the merchant before they depart at midnight.
            </p>
          </div>
          <ChevronRight size={18} className="text-orange-400/70 shrink-0" />
        </div>
      )}

      {playTest && (
        <div data-testid="play-test-banner" className="mb-6 flex items-center gap-3 border border-sky-500/60 bg-sky-950/25 px-4 py-3 rounded-sm">
          <FlaskConical size={18} className="text-sky-400 shrink-0" />
          <p className="text-sm text-sky-100">
            <span className="font-cinzel uppercase tracking-wide text-sky-300">The game is currently in testing.</span>
            {" "}Please report all issues that you find via the bug report form on the{" "}
            <button onClick={() => navigate("/vip")} className="underline text-sky-300 hover:text-sky-200">VIP page</button>.
          </p>
        </div>
      )}

      {data.inactivity_rest && data.inactivity_rest.away_days >= 14 && (
        <div data-testid="inactivity-rest-banner" className="mb-6 flex items-start gap-3 border border-indigo-500/50 bg-indigo-950/25 px-4 py-3 rounded-sm">
          <Moon size={18} className="text-indigo-300 shrink-0 mt-0.5" />
          <p className="flex-1 text-sm text-indigo-100">
            <span className="font-cinzel uppercase tracking-wide text-indigo-300">Welcome back, lanista.</span>
            {" "}You were away {data.inactivity_rest.away_days} days, so your gladiators <span className="font-semibold">rested rather than trained</span>
            {data.inactivity_rest.declined ? " and slipped slightly without your guidance" : " — no progress was lost"}.
            {" "}Set their schedules and get them back to work.
          </p>
          <button data-testid="dismiss-rest-notice-btn" onClick={dismissRestNotice}
            className="shrink-0 text-indigo-400/70 hover:text-indigo-200 transition-colors" title="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      {data.frag_event?.active && (
        <div data-testid="frag-event-banner" className="mb-6 flex items-center gap-3 border border-amber-500/60 bg-amber-950/25 px-4 py-3 rounded-sm">
          <Gem size={18} className="text-amber-400 shrink-0" />
          <p className="text-sm text-amber-100">
            <span className="font-cinzel uppercase tracking-wide text-amber-300">{data.frag_event.multiplier}× Fragment Event live!</span>
            {" "}Every arena fight drops <span className="font-semibold">{data.frag_event.multiplier}×</span> salvage fragments. Go win some battles, lanista.
          </p>
        </div>
      )}

      {data.skill_event?.active && (
        <div data-testid="skill-event-banner" className="mb-6 flex items-center gap-3 border border-emerald-500/60 bg-emerald-950/25 px-4 py-3 rounded-sm">
          <Dumbbell size={18} className="text-emerald-400 shrink-0" />
          <p className="text-sm text-emerald-100">
            <span className="font-cinzel uppercase tracking-wide text-emerald-300">{data.skill_event.multiplier}× Skill Gain Event live!</span>
            {" "}Your gladiators train <span className="font-semibold">{data.skill_event.multiplier}×</span> faster — set an aggressive schedule and push them hard.
          </p>
        </div>
      )}

      {data.scout_event?.active && (
        <div data-testid="scout-event-banner" className="mb-6 flex items-center gap-3 border border-sky-500/60 bg-sky-950/25 px-4 py-3 rounded-sm">
          <Search size={18} className="text-sky-400 shrink-0" />
          <p className="text-sm text-sky-100">
            <span className="font-cinzel uppercase tracking-wide text-sky-300">{data.scout_event.multiplier}× Faster Scouting live!</span>
            {" "}The scouts are working overtime! Every gladiator's potential is read <span className="font-semibold">{data.scout_event.multiplier}×</span> faster this week. Train and scout your prospects to sharpen the read.
          </p>
        </div>
      )}

      <div data-testid="dashboard-colosseum-strip" className="relative mb-8 overflow-hidden rounded-sm border border-amber-900/30">
        <img src="https://images.unsplash.com/photo-1511163262182-1b04e5fa4caa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYW5jaWVudCUyMHJvbWFuJTIwY29sb3NzZXVtfGVufDB8fHx8MTc4MTQwMDUzMXww&ixlib=rb-4.1.0&q=85"
             alt="arena" className="w-full h-24 sm:h-28 object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/84 to-zinc-950/45 flex items-end px-4 py-3 sm:px-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Arena Calendar</div>
            <p className="mt-1 text-sm text-zinc-300">1 real month = 1 game year.</p>
            <ServerClock />
          </div>
        </div>
      </div>

      <AboutBox about={data.about} canEdit endpoint="/settings/about"
        onSaved={(about) => setData((p) => ({ ...p, about }))}
        headerExtra={
          <div data-testid="dash-customize-actions" className="flex flex-wrap items-center justify-end gap-2">
            {user?.name_changed !== true && (
              <span data-testid="dash-free-name-change-alert" className="border border-amber-600/70 bg-amber-950/45 px-2 py-1 text-[10px] uppercase tracking-widest text-amber-200">
                Free name change
              </span>
            )}
            <button data-testid="dash-customize-btn" onClick={() => navigate("/customize")}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-amber-300 hover:text-amber-200 border border-amber-700/60 hover:border-amber-500 px-2.5 py-1 transition-colors">
              <Palette size={12} /> Customize
            </button>
          </div>
        }
        placeholder="Tell other lanistae about yourself…"
        testid="dash-manager-about"
        maxLength={500}
        rows={5}
        wrapperClassName="mb-8"
      />

      {/* New Player Checklist */}
      <NewPlayerChecklist />

      <section data-testid="dashboard-management-section" className="mb-8 rounded-sm border border-amber-900/30 bg-zinc-950/45 p-4 sm:p-6">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Ludus Management</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <VivariumDashboardLink vivariumData={vivariumData} loading={vivariumLoading} />
          <SanctumDashboardLink sanctumData={sanctumData} loading={sanctumLoading} />

          <div data-testid="dashboard-inventory-preview" className="h-full rounded-sm border border-amber-900/30 bg-zinc-950/65 p-4">
            <div className="flex items-center justify-between mb-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Supplies</div>
                <h2 className="mt-1 font-cinzel text-lg text-amber-200 flex items-center gap-2">
                  <Package size={16} className="text-amber-400" />
                  Lanista Inventory
                </h2>
                <p className="mt-1 text-xs text-zinc-500">{consumables.length} item types · {totalConsumableCount.toLocaleString()} total supplies</p>
              </div>
              <button
                data-testid="dashboard-inventory-open-button"
                onClick={() => navigate("/inventory")}
                className="text-xs text-zinc-400 hover:text-amber-300 transition-colors flex items-center gap-1"
                title="View all inventory"
              >
                View All <ExternalLink size={12} />
              </button>
            </div>

            {consumables.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-5 text-center">
                <Package size={32} className="text-zinc-700 mb-2" />
                <p className="text-sm text-zinc-500">No items yet</p>
                <p className="text-xs text-zinc-600 mt-1">Inventory items will appear here</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {paginatedInventory.map((item) => {
                    const getItemNavigation = (itemId) => {
                      if (itemId === 'instant_scout') return '/roster?useItem=instant_scout';
                      if (itemId === 'campaign_stamina_potion') return '/campaign';
                      if (itemId === 'recovery_ether') return '/roster?useItem=recovery_ether';
                      if (itemId === 'love_potion') return '/vivarium';
                      if (itemId.startsWith('beast_food_')) return '/vivarium';
                      if (itemId === 'experienced_medicus') return '/roster?useItem=experienced_medicus';
                      if (itemId === 'common_weapon_chest' || itemId === 'common_armor_chest') return '/inventory';
                      if (itemId === 'uncommon_weapon_chest' || itemId === 'uncommon_armor_chest') return '/inventory';
                      if (itemId === 'rare_weapon_chest' || itemId === 'rare_armor_chest') return '/inventory';
                      if (itemId === 'elite_weapon_chest' || itemId === 'elite_armor_chest') return '/inventory';
                      if (itemId === 'legendary_weapon_chest' || itemId === 'legendary_armor_chest') return '/inventory';
                      return '/inventory';
                    };

                    const getItemIcon = (itemId) => {
                      if (itemId === 'instant_scout') return '/instant-scout.png';
                      if (itemId === 'campaign_stamina_potion') return '/campaign-stamina-v2.png';
                      if (itemId === 'recovery_ether') return '/recovery-ether.png';
                      if (itemId === 'love_potion') return '/love-potion.png';
                      if (itemId.startsWith('beast_food_')) return '/beast-food-v3.png';
                      if (itemId === 'beast_supplement') return '/beast-supplement.png';
                      if (itemId === 'experienced_medicus') return '/medicus.png';
                      if (itemId === 'common_weapon_chest' || itemId === 'common_armor_chest') return '/common-chest.png';
                      if (itemId === 'uncommon_weapon_chest' || itemId === 'uncommon_armor_chest') return '/uncommon-chest.png';
                      if (itemId === 'rare_weapon_chest' || itemId === 'rare_armor_chest') return '/rare-chest.png';
                      if (itemId === 'elite_weapon_chest' || itemId === 'elite_armor_chest') return '/elite-chest.png';
                      if (itemId === 'legendary_weapon_chest' || itemId === 'legendary_armor_chest') return '/legendary-chest.png';
                      return null;
                    };

                    return (
                      <div
                        key={item.id}
                        data-testid={`inventory-item-${item.id}`}
                        className="aspect-square rounded-sm border border-amber-700/30 bg-zinc-900/50 hover:border-amber-600/50 transition-colors cursor-pointer flex flex-col items-center justify-center relative p-1"
                        onClick={() => navigate(getItemNavigation(item.id))}
                        title={`${item.name} - Click to use`}
                      >
                        <img src={getItemIcon(item.id)} alt={item.name} className="w-full h-full object-contain p-1" />
                        <span className="absolute bottom-0.5 right-0.5 px-1 py-0 bg-amber-900/90 text-amber-300 text-[9px] font-semibold rounded-sm leading-tight">
                          {item.count}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {totalInventoryPages > 1 && (
                  <div className="flex items-center justify-between gap-2 border-t border-zinc-800/70 pt-2">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-600">Preview page {inventoryPage} of {totalInventoryPages}</span>
                    <button
                      onClick={() => setInventoryPage((p) => Math.max(1, p - 1))}
                      disabled={inventoryPage === 1}
                      className="p-1 text-zinc-400 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setInventoryPage((p) => Math.min(totalInventoryPages, p + 1))}
                      disabled={inventoryPage === totalInventoryPages}
                      className="p-1 text-zinc-400 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="h-full">
            <ReferralPanel />
          </div>
        </div>
      </section>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="font-cinzel text-lg text-amber-300 uppercase tracking-wide">Your Champions</h2>
        <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row">
          <button data-testid="dash-challenge-btn" onClick={() => navigate("/challenges")}
            className="inline-flex w-full items-center justify-center gap-2 px-5 py-2.5 font-cinzel uppercase tracking-[0.15em] text-sm rounded-sm border border-amber-600/60 text-amber-300 hover:bg-amber-900/30 hover:border-amber-500 transition-colors lg:w-auto">
            <Swords size={15} /> Send Challenge
          </button>
          <button data-testid="dash-recruit-btn" onClick={() => navigate("/recruit")}
            className="inline-flex w-full items-center justify-center gap-2 px-5 py-2.5 font-cinzel uppercase tracking-[0.15em] text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors recruit-pulse lg:w-auto">
            <UserPlus size={15} /> Recruit Pool
          </button>
        </div>
      </div>

      {data.active.length === 0 ? (
        <Empty label="You need gladiators, Lanista! Head to the recruit pool now!">
          <button data-testid="empty-recruit-btn" className={btn()} onClick={() => navigate("/recruit")}>To the Recruit Pool</button>
        </Empty>
      ) : (
        <>
          {/* Sort Controls */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
              <ArrowUpDown size={12} /> Sort by:
            </span>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "age", label: "Age" },
                { id: "stats", label: "Total Stats" },
                { id: "rank", label: "Rank" },
                { id: "potential", label: "Potential" }
              ].map(({ id, label }) => (
                <button
                  key={id}
                  data-testid={`dash-sort-${id}-btn`}
                  onClick={() => changeSortBy(id)}
                  className={`px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide rounded-sm transition-colors ${
                    sortBy === id
                      ? "bg-amber-500 text-zinc-950"
                      : "border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {(() => {
              const sorted = [...data.active];
              if (sortBy === "stats") {
                sorted.sort((a, b) => (b.total_skill || 0) - (a.total_skill || 0));
              } else if (sortBy === "rank") {
                sorted.sort((a, b) => (a.rank || 999999) - (b.rank || 999999));
              } else if (sortBy === "potential") {
                // Priority 1: Avoid rapid_decline
                // Priority 2: Fully scouted
                // Priority 3: Potential grade (Elite > Excellent > Good > Average > Poor)
                const gradeValue = (grade) => {
                  if (!grade) return 0;
                  if (grade === "Elite") return 5;
                  if (grade === "Excellent") return 4;
                  if (grade === "Good") return 3;
                  if (grade === "Average") return 2;
                  if (grade === "Poor") return 1;
                  return 0;
                };
                sorted.sort((a, b) => {
                  // Rapid decline goes to bottom
                  const aRapid = a.lifecycle === "rapid_decline";
                  const bRapid = b.lifecycle === "rapid_decline";
                  if (aRapid && !bRapid) return 1;
                  if (!aRapid && bRapid) return -1;
                  
                  // Then by fully scouted
                  const aFullyScouted = (a.potential_band?.exact === true);
                  const bFullyScouted = (b.potential_band?.exact === true);
                  if (aFullyScouted && !bFullyScouted) return -1;
                  if (!aFullyScouted && bFullyScouted) return 1;
                  
                  // Then by potential grade
                  return gradeValue(b.potential_grade) - gradeValue(a.potential_grade);
                });
              } else {
                // age - keep default backend sort (oldest first)
                sorted.sort((a, b) => (b.age || 0) - (a.age || 0));
              }
              return <DashboardChampionRows gladiators={sorted} onSelect={openGlad} onTrain={(gladiator) => navigate(`/training?gladiator=${encodeURIComponent(gladiator.id)}`)} />;
            })()}
          </div>
        </>
      )}

      {/* Gladiator Modal - Full functionality */}
      <Modal open={!!sel} onClose={() => setSel(null)} title={
        sel ? (
          <button data-testid="dash-glad-link" onClick={() => { const id = sel.id; setSel(null); navigate(`/gladiator/${id}`); }} className="hover:text-amber-100 transition-colors underline decoration-amber-700/40 underline-offset-4">
            {sel.name}
          </button>
        ) : ""
      } wide>
        {sel && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {(() => { const I = CLASS_META[sel.cls]?.icon; return I ? <I className={CLASS_META[sel.cls].color} size={20} /> : null; })()}
                <span className="font-cinzel text-amber-300 uppercase tracking-wide">{sel.cls}</span>
                <LifecycleBadge lifecycle={sel.lifecycle} />
              </div>
              <p className="text-sm text-zinc-400 mb-4">{sel.class_info?.desc}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Age" value={sel.age} />
                <Info label="Peak Overall" value={sel.peak_overall} />
                <Info label="Current Overall" value={sel.overall} />
                <Info label="Rating" value={sel.rating} />
                <Info label="Record" value={`${sel.wins}W / ${sel.losses}L`} />
                <Info label="Potential" value={sel.potential_grade} />
                <Info label="Weapon" value={sel.class_info?.weapon} />
                <Info label="Offhand" value={sel.class_info?.offhand || "—"} />
              </div>

              {sel.potential_band && !sel.potential_band.exact && (
                <div className="mt-3 border border-violet-900/40 bg-violet-950/20 px-3 py-2">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-violet-300/80">
                    <span className="flex items-center gap-1.5"><Search size={12} /> Scouting</span>
                    <span>{Math.round((sel.potential_band.progress || 0) * 100)}%</span>
                  </div>
                  <div className="w-full h-1 bg-zinc-800 mt-1.5 overflow-hidden">
                    <div className="h-full bg-violet-500 transition-all" style={{ width: `${Math.round((sel.potential_band.progress || 0) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    {sel.potential_band.revealed
                      ? <>Estimated potential <span className="text-violet-300">{sel.potential_band.label}</span> — keep training to sharpen the read.</>
                      : <>Not yet scouted. Train this fighter to begin reading their potential.</>}
                  </p>
                  <button data-testid="scout-gladiator-btn" onClick={() => scoutNoConfirm ? doScout() : setScoutOpen(true)} disabled={busy}
                    title="Send scouts to narrow this gladiator's potential range"
                    className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1.5 border border-violet-700/50 text-violet-300 hover:bg-violet-900/30 hover:border-violet-500 rounded-sm transition-colors disabled:opacity-50">
                    <Search size={12} /> Scout (+25%) — 100g
                  </button>
                </div>
              )}

              <div className="mt-4">
                {sel.renamed ? (
                  <p className="text-[11px] text-zinc-600 flex items-center gap-1.5"><Pencil size={12} /> This gladiator has used their one rename.</p>
                ) : (
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Pencil size={12} /> Rename (one time only)</label>
                    <div className="flex gap-2 mt-1.5">
                      <input data-testid="rename-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} maxLength={40}
                        placeholder="New name" className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
                      <button data-testid="rename-btn" disabled={busy || !renameVal.trim()} onClick={doRename} className={btn() + " py-2 px-4"}>Save</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <AboutBox about={sel.about} canEdit endpoint={`/gladiators/${sel.id}/about`}
                  onSaved={(about) => { setSel((s) => ({ ...s, about })); setData((prev) => ({ ...prev, active: prev.active.map((x) => x.id === sel.id ? { ...x, about } : x) })); }}
                  placeholder="Describe this gladiator's story, fighting style, or legend…" testid="dash-glad-about" />
              </div>

              {sel.auctioned && (
                <p data-testid="auctioned-settings-lock" className="mt-1 mb-1 text-[11px] text-fuchsia-300/90 flex items-center gap-1.5 border border-fuchsia-800/40 bg-fuchsia-950/20 px-2.5 py-1.5 rounded-sm">
                  <Gavel size={12} /> Listed at the Auction House — skills & loadout stay public and auto-accept is locked until the listing ends.
                </p>
              )}
              {sel.golden_crown && (
                <p data-testid="crown-holder-lock" className="mt-1 mb-1 text-[11px] text-amber-300/90 flex items-center gap-1.5 border border-amber-800/40 bg-amber-950/20 px-2.5 py-1.5 rounded-sm">
                  <GoldenCrownIcon size={12} /> Golden Crown Holder — auto-accept is mandatory and cannot be disabled while holding the crown.
                </p>
              )}
              <button data-testid="toggle-skills-visibility-btn" onClick={toggleSkills} disabled={busy || sel.auctioned}
                className="mt-1 flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-2 transition-colors w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-zinc-400 disabled:hover:border-zinc-700">
                {sel.show_skills === false ? <><EyeOff size={14} /> Skills hidden — make public</> : <><Eye size={14} /> Skills public — hide from others</>}
              </button>
              <button data-testid="toggle-auto-accept-btn" onClick={toggleProtect} disabled={busy || sel.golden_crown || sel.auctioned}
                className="mt-1 flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-3 py-2 transition-colors w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-zinc-400 disabled:hover:border-zinc-700">
                {sel.protected_from_challenges ? <><ShieldOff size={14} /> Auto-accept OFF — enable it</> : <><ShieldCheck size={14} /> Auto-accept ON — disable it</>}
              </button>
            </div>

            <div>
              <button data-testid="roster-view-full-btn" onClick={() => { setSel(null); navigate("/roster"); }}
                className={btn("ghost") + " w-full mb-3 flex items-center justify-center gap-2"}>
                <HelmetIcon size={16} /> View on Active Roster
              </button>
              <button data-testid="equipment-btn" onClick={() => { setSel(null); navigate("/equipment"); }}
                className={btn("ghost") + " w-full mb-3 flex items-center justify-center gap-2"}>
                <Hammer size={16} /> Manage Equipment
              </button>
              <button data-testid="training-btn" onClick={() => { const gladiatorId = sel?.id; setSel(null); navigate(`/training?gladiator=${encodeURIComponent(gladiatorId)}`); }}
                className={btn("ghost") + " w-full flex items-center justify-center gap-2"}>
                <Dumbbell size={16} /> Training Schedule
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Scout Confirmation Modal */}
      <Modal open={scoutOpen} onClose={() => setScoutOpen(false)} title="Send Scouts">
        <p className="text-sm text-zinc-300 mb-4">
          Send scouts to tighten <span className="font-cinzel text-amber-300">{sel?.name}</span>'s potential read by <span className="text-violet-300">+25%</span>.
        </p>
        <p className="text-sm text-zinc-400 mb-4">Cost: <span className="text-amber-300">100 gold</span></p>
        <label className="flex items-center gap-2 text-sm text-zinc-400 mb-4 cursor-pointer">
          <input type="checkbox" checked={scoutNoConfirm} onChange={(e) => setScoutNoConfirm(e.target.checked)} className="form-checkbox" />
          <span>Don't ask again</span>
        </label>
        <div className="flex gap-2">
          <button onClick={() => setScoutOpen(false)} className={btn("ghost") + " flex-1"}>Cancel</button>
          <button onClick={doScout} disabled={busy} className={btn() + " flex-1"}>
            {busy ? "Scouting..." : "Scout"}
          </button>
        </div>
      </Modal>

      {/* Trait Selection Modal */}
      {traitModalGlad && (
        <TraitSelectionModal
          gladiator={traitModalGlad}
          onConfirm={handleAssignTraits}
          onClose={() => setTraitModalGlad(null)}
        />
      )}
      </div>
    </div>
  );
}